function formatValue(value, options = {}) {
  if (value === null || value === undefined || value === "") return "—";

  const { decimals = 2, percent = false, thousandSeparator = true } = options;

  if (typeof value === "number") {
    const formatted = thousandSeparator
      ? value.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals,
        })
      : value.toFixed(decimals);

    return percent ? `${formatted}%` : formatted;
  }

  return String(value);
}

function IconWrapper({ children }) {
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 shadow-sm ring-1 ring-slate-200">
      <div className="h-6 w-6 text-slate-700">{children}</div>
    </div>
  );
}

function FactoryIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 21V10l6 3V10l6 3V5l6 4v12" />
      <path d="M3 21h18" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6l4 2" />
    </svg>
  );
}

function TrendingUpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 16l6-6 4 4 6-8" />
      <path d="M16 6h4v4" />
    </svg>
  );
}

function PercentCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 16l8-8" />
      <circle cx="9" cy="9" r="1.3" />
      <circle cx="15" cy="15" r="1.3" />
    </svg>
  );
}

function PriceTagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 10l-8.5 8.5a2 2 0 0 1-2.8 0L3.5 13.3a2 2 0 0 1 0-2.8L12 2h8v8Z" />
      <circle cx="16.5" cy="7.5" r="1.2" />
    </svg>
  );
}

function RevenueIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 19h16" />
      <path d="M6 16l4-5 3 3 5-7" />
      <path d="M18 7h2v2" />
    </svg>
  );
}

function MetricCard({ title, value, unit, icon, accent = "from-teal-500 to-indigo-500" }) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`}
      />
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{title}</p>
        </div>
        <IconWrapper>{icon}</IconWrapper>
      </div>

      <div className="space-y-2">
        <div className="text-3xl font-bold tracking-tight text-slate-900">
          {value}
        </div>
        <div className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
          {unit}
        </div>
      </div>
    </div>
  );
}

export default function EconomicIndicatorsPanel({
  totalCapitalRequirements,
  payOutTime,
  roi,
  irr,
  minimumSellingPrice,
  revenues,
  title = "Economic Indicators",
  subtitle = "",
}) {
  const items = [
    {
      title: "Total Capital Requirements",
      value: formatValue(totalCapitalRequirements.value, { decimals: 3 }),
      unit: totalCapitalRequirements.unit,
      icon: <FactoryIcon />,
      accent: "from-teal-500 to-indigo-500",
    },
    {
      title: "Pay Out Time, Before Tax",
      value: formatValue(payOutTime.value, { decimals: 2 }),
      unit: payOutTime.unit,
      icon: <ClockIcon />,
      accent: "from-indigo-500 to-blue-500",
    },
    {
      title: "Return on Investment (ROI)",
      value: formatValue(roi.value, { decimals: 2, percent: true }),
      unit: roi.unit,
      icon: <TrendingUpIcon />,
      accent: "from-blue-500 to-green-500",
    },
    {
      title: "IRR",
      value: formatValue(irr.value, { decimals: 2, percent: true }),
      unit: irr.unit,
      icon: <PercentCircleIcon />,
      accent: "from-pink-500 to-red-500",
    },
    {
      title: "Minimum Selling Price",
      value: formatValue(minimumSellingPrice.value, { decimals: 0 }),
      unit: minimumSellingPrice.unit,
      icon: <PriceTagIcon />,
      accent: "from-red-500 to-orange-500",
    },
    {
      title: "Revenues",
      value: formatValue(revenues.value, { decimals: 3 }),
      unit: revenues.unit,
      icon: <RevenueIcon />,
      accent: "from-orange-500 to-pink-500",
    },
  ];

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm my-5">
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 md:text-2xl">
            {title}
          </h2>
        </div>

      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <MetricCard key={item.title} {...item} />
        ))}
      </div>
    </section>
  );
}